import React, { forwardRef, TextareaHTMLAttributes, useImperativeHandle, useRef, useState } from 'react';
import Countdown from '../countdown-component/countdown-component';
import styles from './text-area-component.module.scss';

type TextAreaProps = {
  label?: string;
  name?: string;
  helpText?: string;
  background?: 'white' | 'grey';
  maxCharacters?: number;
  /** Refer to 'Custom Max Character Text' story */
  maxCharactersText?: string;
  displayCharacterLimit?: boolean;
  errorMessage?: string;
  isValid?: boolean;
  textareaClassName?: string;
  isResizableVertically?: boolean;
  validationCallback?: (value: string) => boolean;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

const DEFAULT_PROPS: Required<Pick<TextAreaProps, 'maxCharactersText'>> = {
  maxCharactersText: '|no_of_characters| characters max',
};

export interface TextAreaRef {
  focus(): void;
}

/**
 * Text Area component
 *
 * https://www.figma.com/file/jiN4FkdkhDZIWxIkPS6Ru7/design-system?node-id=32%3A0
 */
export const TextArea = forwardRef<TextAreaRef, TextAreaProps>((props: TextAreaProps, ref) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const {
    label,
    name,
    helpText,
    background,
    maxCharacters,
    maxCharactersText,
    displayCharacterLimit,
    className,
    errorMessage,
    isValid,
    textareaClassName,
    isResizableVertically,
    validationCallback,
    onBlur,
    ...textAreaAttributes
  } = { ...DEFAULT_PROPS, ...props }; // <-- merge defaults before destructuring;

  const [isValidState, setIsValidState] = useState(isValid ?? true);
  const [lastIsValidStateProp, setLastIsValidStateProp] = useState(isValid);
  const [count, setCount] = useState<number>(20);
  const [lastMaxCharactersProp, setLastMaxCharactersProp] = useState(maxCharacters);

  // exposes internal input methods via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
      }
    },
  }));

  // update state if isValid changes
  if (isValid !== lastIsValidStateProp) {
    setIsValidState(isValid ?? true);
    setLastIsValidStateProp(isValid);
  }

  //always reset count limit to 20 when max characters is changed
  if (lastMaxCharactersProp !== maxCharacters) {
    setCount(20);
    setLastMaxCharactersProp(maxCharacters);
  }

  return (
    <div className={`${styles['text-area-component']} ${textAreaAttributes.disabled ? styles.disabled : ''} ${className ? className : ''}`}>
      {label ? <label htmlFor={name}>{label}</label> : null}

      {helpText && <p className={styles['help-text']}>{helpText}</p>}

      {maxCharactersText && <p className={`${styles['help-text']} ${styles['max-characters']}`}>{substitueMaxCharacters()}</p>}

      <div className={styles['text-area-container']}>
        <div className={`${styles['text-area-wrapper']} ${isResizableVertically ? styles.resizable : styles['non-resizable']}`}>
          {isResizableVertically && (
            <div className={styles.resizer}>
              <svg width='17' height='18' viewBox='0 0 17 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <rect x='11.3433' y='0.970581' width='2' height='16' transform='rotate(45 11.3433 0.970581)' fill='#C4C4C4' />
                <rect x='12.7576' y='8.04163' width='2' height='8' transform='rotate(45 12.7576 8.04163)' fill='#C4C4C4' />
              </svg>
            </div>
          )}
          <textarea
            onBlur={onFocusLost}
            className={`${styles['text-area']} ${background ? styles[background] : styles.grey} ${!isValidState ? styles.error : ''} ${
              textareaClassName ?? ''
            }`}
            {...textAreaAttributes}
            ref={textAreaRef}
            maxLength={maxCharacters}
            disabled={textAreaAttributes.disabled}
            onChange={(e) => countChar(e)}
          />
        </div>

        {maxCharacters && displayCharacterLimit && (
          <div className={styles['character-limit-container']}>
            <Countdown count={count} />
          </div>
        )}

        {errorMessage && !isValidState && <p className={styles['error-text']}>{errorMessage}</p>}
      </div>
    </div>
  );

  function countChar(e: React.ChangeEvent<HTMLTextAreaElement>) {
    if (maxCharacters && displayCharacterLimit) {
      const len = e.currentTarget.value.length;
      const charactersLeft = maxCharacters - len;

      //if 20 or less characters left update the count
      if (charactersLeft <= 20) {
        setCount(charactersLeft);
      }
    }
  }

  function onFocusLost(e: React.FocusEvent<HTMLTextAreaElement>) {
    if (validationCallback) {
      setIsValidState(validationCallback(e.currentTarget.value));
    }

    // in case we have another blur callback
    onBlur?.(e);
  }

  function substitueMaxCharacters() {
    if (maxCharactersText && maxCharacters) return maxCharactersText.replace(/\|no_of_characters\|/, maxCharacters.toString());
  }
});

export default TextArea;
